import { AlertTriangle, ArrowRight, CalendarDays, Clock3, RefreshCw } from 'lucide-react';
import { Button } from '../ui/button';

/**
 * Active staff home is deliberately small. Real recruitment, compliance,
 * shift approval, geo-verification and document history stay in their existing
 * permissioned workflows, accessed via the dedicated navigation.
 */
export function deriveWorkerTodaySummary({
  shiftGroups = {},
  availableShifts = [],
  alerts = [],
  expiredTrainings = [],
  now = new Date(),
} = {}) {
  const timeNow = now instanceof Date ? now.getTime() : new Date(now).getTime();
  const upcomingShifts = (shiftGroups.active || []).filter((item) => {
    const end = Date.parse(item?.shift?.end_at || '');
    return Number.isFinite(end) && end >= timeNow;
  });
  const nextShift = [...upcomingShifts].sort(
    (a, b) => Date.parse(a.shift.start_at || '') - Date.parse(b.shift.start_at || '')
  )[0] || null;
  const pendingCount = (shiftGroups.pending || []).length;
  // Exclude duplicates when the expiry collection and alerts describe the
  // same requirement; the detailed Alert page remains the source of truth.
  const expiredItems = new Map();
  for (const item of expiredTrainings || []) {
    const key = String(item?.id || item?.code || item?.name || '').trim();
    if (key && !expiredItems.has(key)) expiredItems.set(key, item);
  }
  return {
    nextShift,
    pendingCount,
    availableCount: (availableShifts || []).length,
    alertCount: (alerts || []).length,
    expired: [...expiredItems.values()],
  };
}

export default function WorkerTodayHome({
  shiftGroups,
  availableShifts,
  alerts,
  expiredTrainings,
  formatDateTime,
  onOpenShift,
  onShifts,
  onAlerts,
  onRenew,
  uploading,
  renewalFeedback = null,
}) {
  const summary = deriveWorkerTodaySummary({
    shiftGroups, availableShifts, alerts, expiredTrainings,
  });
  const { nextShift, pendingCount, availableCount, alertCount, expired } = summary;

  return (
    <section className="space-y-3" aria-label="Worker today" data-testid="worker-today-home">
      {renewalFeedback?.requirementId?.startsWith('training_') && renewalFeedback?.message && (
        <div
          role={renewalFeedback.status === 'error' ? 'alert' : 'status'}
          aria-live="polite"
          className={`rounded-xl border p-3 text-sm ${renewalFeedback.status === 'error' ? 'border-red-200 bg-red-50 text-red-900' : 'border-teal-200 bg-teal-50 text-teal-900'}`}
          data-testid="worker-today-renewal-feedback"
        >
          {renewalFeedback.message}
        </div>
      )}
      <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm" data-testid="worker-next-shift">
        <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-[#0F5C5E]">
          <CalendarDays className="h-4 w-4" /> Your next shift
        </div>
        {nextShift ? (
          <>
            <p className="mt-3 text-lg font-semibold text-slate-900">
              {formatDateTime(nextShift.shift.start_at)}
            </p>
            <p className="mt-1 text-sm text-slate-600">
              {nextShift.shift.role_required || 'Scheduled work'}
            </p>
            <p className="mt-1 text-xs text-slate-500">
              Open your shift for the full assignment, attendance and care tasks.
            </p>
            <Button className="mt-4 min-h-11 w-full sm:w-auto" onClick={() => onOpenShift(nextShift)} data-testid="worker-open-next-shift">
              Open shift <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          </>
        ) : (
          <>
            <p className="mt-3 text-sm font-medium text-slate-800">No upcoming assigned shifts</p>
            <p className="mt-1 text-xs text-slate-500">You can look for available shifts in Shifts.</p>
            <Button className="mt-4 min-h-11 w-full sm:w-auto" onClick={onShifts} data-testid="worker-find-shifts">
              View shifts <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          </>
        )}
      </div>

      {(pendingCount > 0 || alertCount > 0 || expired.length > 0) && (
        <div className="rounded-2xl border border-amber-200 bg-amber-50/60 p-4 shadow-sm" data-testid="worker-today-attention">
          <h2 className="flex items-center gap-2 text-sm font-semibold text-amber-950">
            <AlertTriangle className="h-4 w-4" /> Needs attention
          </h2>
          <div className="mt-3 space-y-2">
            {pendingCount > 0 && (
              <div className="flex items-center justify-between gap-2 rounded-lg bg-white p-3">
                <p className="text-sm text-slate-800">{pendingCount} shift offer{pendingCount === 1 ? '' : 's'} waiting for a response</p>
                <Button size="sm" variant="outline" onClick={onShifts} data-testid="worker-review-offers">Review</Button>
              </div>
            )}
            {expired.length > 0 && (
              <div className="flex items-center justify-between gap-2 rounded-lg bg-white p-3">
                <p className="min-w-0 text-sm text-slate-800">
                  {expired.length} expired certificate{expired.length === 1 ? '' : 's'}
                </p>
                {expired[0]?.id || expired[0]?.code ? (
                  <Button
                    size="sm"
                    variant="outline"
                    disabled={Boolean(uploading) || (
                      renewalFeedback?.status === 'success'
                      && renewalFeedback.requirementId === `training_${expired[0].id || expired[0].code}`
                    )}
                    onClick={() => onRenew(expired[0])}
                    data-testid="active-worker-renew-certificate"
                  >
                    <RefreshCw className="mr-1 h-4 w-4" /> Renew
                  </Button>
                ) : (
                  <Button size="sm" variant="outline" onClick={onAlerts}>Review</Button>
                )}
              </div>
            )}
            {alertCount > 0 && (
              <div className="flex items-center justify-between gap-2 rounded-lg bg-white p-3">
                <p className="text-sm text-slate-800">{alertCount} work-readiness reminder{alertCount === 1 ? '' : 's'}</p>
                <Button size="sm" variant="outline" onClick={onAlerts} data-testid="worker-review-alerts">Review</Button>
              </div>
            )}
          </div>
        </div>
      )}

      <div className="grid grid-cols-2 gap-2">
        <Button variant="outline" className="min-h-12 gap-2" onClick={onShifts} data-testid="worker-today-shifts">
          <CalendarDays className="h-4 w-4" />
          Shifts{availableCount > 0 ? ` (${availableCount} open)` : ''}
        </Button>
        <Button variant="outline" className="min-h-12 gap-2" onClick={onAlerts} data-testid="worker-today-alerts">
          <Clock3 className="h-4 w-4" /> Alerts
        </Button>
      </div>
    </section>
  );
}
